import { msg } from '@lingui/core/macro';
import { Trans, useLingui } from '@lingui/react/macro';
import { Box, Chip, Divider, Link, Stack, Tooltip, Typography } from '@mui/material';
import { useMemo } from 'react';
import { marketChargesCloseFee, resolveTierFeeMicros } from '@/common/MarketData';
import useAppStore, { useSelectedMarket } from '../../../store/rootStore';

// Keep in sync with packages/ui-kit/.../fee_tiers_dialog.tsx until those values come from the API.
const TIER_DISPLAY: { label: string; volume: string; makerRebateBps?: number }[] = [
  { label: '1', volume: '< $5M' },
  { label: '2', volume: '≥ $5M' },
  { label: '3', volume: '≥ $25M' },
  { label: '4', volume: '≥ $100M' },
  { label: '5', volume: '≥ $250M', makerRebateBps: -0.01 },
  { label: 'VIP 1', volume: '≥ $500M', makerRebateBps: -0.1 },
  { label: 'VIP 2', volume: '≥ $1B', makerRebateBps: -0.5 },
];

/** Display label for the account fee-tier index (`account.ft`), e.g. "1" or "VIP 1". */
export function getAccountFeeTierLabel(feeTier: number | undefined | null): string | undefined {
  if (feeTier == null || !Number.isFinite(feeTier)) return undefined;
  const idx = Math.min(Math.max(Math.trunc(feeTier), 0), TIER_DISPLAY.length - 1);
  return TIER_DISPLAY[idx]?.label;
}

const MAKER_REBATE_TOOLTIP = msg`Maker rebate: you are paid this amount instead of paying a fee. Rebates are applied off-chain and paid out separately, so they arrive with a delay rather than on each fill.`;

const FEES_DOCS_URL = 'https://docs.perpl.xyz/exchange/fees';

const bpsFormatter = new Intl.NumberFormat('en-US', { maximumFractionDigits: 2 });

function formatBpsFromMicros(micros: bigint | undefined): string {
  return micros != null ? bpsFormatter.format(Number(micros) / 100) : '—';
}

function ladderOrBase(ladder: bigint[] | undefined, base: bigint | undefined): bigint[] {
  return ladder ?? (base != null ? [base] : []);
}

const cellSx = { fontWeight: 500, flex: 1, minWidth: 0, fontSize: 11 } as const;
const TIER_COL_MIN_WIDTH = 100;
const VOLUME_COL_MIN_WIDTH = 72;

function TierRow({
  label,
  chip,
  volume,
  makerBps,
  makerTooltip,
  takerBps,
  highlighted,
  header,
}: {
  label: string;
  chip?: boolean;
  volume: string;
  makerBps: string;
  makerTooltip?: string;
  takerBps: string;
  highlighted?: boolean;
  header?: boolean;
}) {
  return (
    <Box
      sx={(theme) => ({
        display: 'flex',
        alignItems: 'center',
        gap: 0.75,
        borderRadius: 1,
        ...(header ? { px: 0.5, py: 0.25 } : { px: 0.5, py: 0.5, minHeight: theme.spacing(3.5) }),
        ...(highlighted && { bgcolor: theme.vars.palette.primary._states.selected }),
      })}
    >
      <Stack direction="row" alignItems="center" spacing={0.5} sx={{ minWidth: TIER_COL_MIN_WIDTH, flexShrink: 0 }}>
        <Typography sx={{ fontSize: 11, fontWeight: header ? 600 : 500 }}>{label}</Typography>
        {chip ? (
          <Chip
            label={<Trans comment="Chip marking the user's own fee tier">You</Trans>}
            size="small"
            sx={{ height: 16, fontSize: 9, '& .MuiChip-label': { px: 0.5 } }}
          />
        ) : null}
      </Stack>
      <Typography
        sx={{ ...cellSx, minWidth: VOLUME_COL_MIN_WIDTH }}
        className={header ? undefined : 'numeric'}
        color={header ? 'text.secondary' : undefined}
      >
        {volume}
      </Typography>
      <Typography sx={cellSx} className={header ? undefined : 'numeric'} color={header ? 'text.secondary' : undefined}>
        {makerTooltip ? (
          <Tooltip title={makerTooltip} arrow placement="top">
            <Box component="span" sx={(theme) => theme.custom.helpText}>
              {makerBps}
            </Box>
          </Tooltip>
        ) : (
          makerBps
        )}
      </Typography>
      <Typography sx={cellSx} className={header ? undefined : 'numeric'} color={header ? 'text.secondary' : undefined}>
        {takerBps}
      </Typography>
    </Box>
  );
}

export function FeesPanel() {
  const { t } = useLingui();
  const selectedMarket = useSelectedMarket();
  const accountFeeTier = useAppStore((state) => state.account?.ft);

  const { visibleTiers, userTier } = useMemo(() => {
    const makerFees = ladderOrBase(selectedMarket?.makerFeesMicros, selectedMarket?.makerFeeMicros);
    const takerFees = ladderOrBase(selectedMarket?.takerFeesMicros, selectedMarket?.takerFeeMicros);
    const tierCount = Math.max(makerFees.length, takerFees.length);
    const tierRows = Array.from({ length: tierCount }, (_, tier) => ({
      tier,
      makerMicros: resolveTierFeeMicros(makerFees, undefined, tier, true),
      takerMicros: resolveTierFeeMicros(takerFees, undefined, tier, true),
    }));
    const visible = tierRows.filter(({ makerMicros, takerMicros }) => (makerMicros ?? 0n) !== 0n || (takerMicros ?? 0n) !== 0n);
    const user = accountFeeTier != null ? Math.min(Math.max(accountFeeTier, 0), Math.max(tierCount - 1, 0)) : undefined;
    return { visibleTiers: visible, userTier: user };
  }, [accountFeeTier, selectedMarket]);

  if (!selectedMarket || visibleTiers.length === 0) {
    return (
      <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>
        <Trans comment="Empty state when fee tiers are unavailable in account menu">Fee schedule unavailable</Trans>
      </Typography>
    );
  }

  return (
    <Stack spacing={1.25}>
      <Box>
        <TierRow
          header
          label={t({ comment: 'Fee table column: tier', message: 'Tier' })}
          volume={t({ comment: 'Fee table column: 14-day volume (compact)', message: '14D Vol' })}
          makerBps={t({ comment: 'Fee table column: maker fee in bps (compact)', message: 'Maker' })}
          takerBps={t({ comment: 'Fee table column: taker fee in bps (compact)', message: 'Taker' })}
        />
        <Divider sx={{ my: 0.5 }} />
        {visibleTiers.map(({ tier, makerMicros, takerMicros }, row) => {
          const display = TIER_DISPLAY[row] ?? { label: String(row + 1), volume: '—' };
          const rebateBps = makerMicros === 0n ? display.makerRebateBps : undefined;
          return (
            <TierRow
              key={display.label}
              label={display.label}
              chip={tier === userTier}
              highlighted={tier === userTier}
              volume={display.volume}
              makerBps={rebateBps != null ? bpsFormatter.format(rebateBps) : formatBpsFromMicros(makerMicros)}
              makerTooltip={rebateBps != null ? t(MAKER_REBATE_TOOLTIP) : undefined}
              takerBps={formatBpsFromMicros(takerMicros)}
            />
          );
        })}
      </Box>
      <Typography sx={{ fontSize: 11, color: 'text.secondary', lineHeight: 1.4 }}>
        {marketChargesCloseFee(selectedMarket) ? (
          <Trans comment="Fee schedule note in account menu: the rates shown are per trade, charged on open and again on close">
            Fees apply on open and again on close.{' '}
            <Link href={FEES_DOCS_URL} target="_blank" rel="noopener" underline="hover">
              Docs
            </Link>
          </Trans>
        ) : (
          <Trans comment="Fee schedule note in account menu">
            Fees apply on open only, not on close.{' '}
            <Link href={FEES_DOCS_URL} target="_blank" rel="noopener" underline="hover">
              Docs
            </Link>
          </Trans>
        )}
      </Typography>
    </Stack>
  );
}
